import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Alert,
  Snackbar,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Description as DocumentIcon,
  Refresh as RefreshIcon,
  Build as LabIcon,
  PersonOutline as AssessorIcon,
  BusinessCenter as DoIcon,
  Description as FileIcon,
  GetApp as DownloadIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import FileUploadComponent from '../FileUploadComponent';
import { additionalDocumentService } from '../../services/additionalDocumentService';
import s3Utils from '../../utils/s3Utils';
import { encryptText, decryptText } from '../../utils/encryption';

/**
 * Common Additional Document Management Component
 * Allows users to save additional documents with document name, target type (Lab/Assessor/DO), and file upload
 */
const AdditionalDocument = ({
  orgId = null,
  appId = null,
  title = "Additional Documents",
  subtitle = "Upload and manage additional documents",
  showExisting = true,
  onDocumentSaved = null,
  onDocumentDeleted = null,
  readOnly = false,
  onBack = null,
  showBackButton = true
}) => {
  // Get URL search parameters
  const [searchParams] = useSearchParams();
  // Support both 'type' and 'targetType' parameters
  let urlType = searchParams.get('type') || searchParams.get('targetType');
  let urlAppId = searchParams.get('appId');
  let urlOrgId = searchParams.get('orgId');
  let urlAssessmentId = searchParams.get('assessmentId');
  
  // Decrypt type parameter
  const decryptedType = urlType ? decryptText(urlType) : null;
  if (decryptedType) {
    urlType = decryptedType;
  }
  
  // Decrypt appId parameter
  const decryptedAppId = urlAppId ? decryptText(urlAppId) : null;
  if (decryptedAppId) {
    urlAppId = decryptedAppId;
  }
  
  // Decrypt orgId parameter
  const decryptedOrgId = urlOrgId ? decryptText(urlOrgId) : null;
  if (decryptedOrgId) {
    urlOrgId = decryptedOrgId;
  }

  // Decrypt assessmentId parameter
  const decryptedAssessmentId = urlAssessmentId ? decryptText(urlAssessmentId) : null;
  if (decryptedAssessmentId) {
    urlAssessmentId = decryptedAssessmentId;
  }
  // Check if upload tab should be shown based on URL type parameter
  const showUploadTab = urlType === 'do' || urlType === 'assessor';
  
  // Form state
  const [formData, setFormData] = useState({
    documentName: '',
    targetType: urlType==='assessor' ? 'do' : urlType || 'lab', // 'lab', 'assessor', 'do'
    description: '',
    uploadedFile: null
  });

  // UI state - Set initial tab based on type parameter
  const [activeTab, setActiveTab] = useState(showUploadTab ? 0 : 0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Documents state
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [editingDocument, setEditingDocument] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.documentName.trim()) {
      newErrors.documentName = 'Document name is required';
    }

    if (!formData.targetType) {
      newErrors.targetType = 'Please select target type';
    }

    if (!formData.uploadedFile && !editingDocument) {
      newErrors.file = 'Please upload a document';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle file upload
  const handleFileUpload = (fieldName, fileData) => {
    setFormData(prev => ({
      ...prev,
      uploadedFile: fileData
    }));
    
    // Clear file error
    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: ''
      }));
    }
  };

  // Get final values (URL parameters take precedence over props)
  const finalOrgId = urlOrgId || orgId;
  const finalAppId = urlAppId || appId;

  // Load existing documents
  const loadExistingDocuments = async () => {
    if (!showExisting || (!finalOrgId && !finalAppId)) return;

    setLoadingDocuments(true);
    try {
      const response = await additionalDocumentService.getDocuments({
        orgId: finalOrgId,
        appId: finalAppId,
        targetType: urlType || null,
        assessmentId: urlAssessmentId || null
      });

      if (response.success) {
        setExistingDocuments(response.data || []);
      } else {
        console.error('Failed to load documents:', response.error);
        setErrorMessage('Failed to load existing documents');
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setErrorMessage('Error loading existing documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Save document
  const handleSave = async () => {
    if (!validateForm()) return;
    if (!finalOrgId) {
      setErrorMessage('Session End Please Login Again');
      return;
    }

    setSaving(true);
    try {
      const documentData = {
        orgId: finalOrgId,
        appId: finalAppId,
        documentName: formData.documentName.trim(),
        targetType: formData.targetType,
        description: formData.description.trim(),
        fileData: formData.uploadedFile,
        uploadedBy: localStorage.getItem('orgId') || '0',
        assessmentId: urlAssessmentId || null
      };

      let response;
      if (editingDocument) {
        response = await additionalDocumentService.updateDocument(editingDocument.id, documentData);
      } else {
        response = await additionalDocumentService.saveDocument(documentData);
      }

      if (response.success) {
        setSuccessMessage(editingDocument ? 'Document updated successfully!' : 'Document saved successfully!');
        
        // Reset form
        setFormData({
          documentName: '',
          targetType: urlType==='assessor' ? 'do' : 'lab',
          description: '',
          uploadedFile: null
        });
        setEditingDocument(null);
        
        // Reload documents
        await loadExistingDocuments();
        
        // Switch to existing documents tab after save (if showing existing and upload tab is available)
        if (showExisting && !readOnly && showUploadTab) {
          setActiveTab(1);
        }
        
        // Notify parent component
        if (onDocumentSaved) {
          onDocumentSaved(response.data);
        }
      } else {
        setErrorMessage(response.error || 'Failed to save document');
      }
    } catch (error) {
      console.error('Error saving document:', error);
      setErrorMessage('Error saving document: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Edit document
  const handleEdit = (document) => {
    setFormData({
      documentName: document.documentName || '',
      targetType: document.targetType || 'lab',
      description: document.description || '',
      uploadedFile: document.fileData || null
    });
    setEditingDocument(document);
    setErrors({});
    
    // Switch to Add Document tab when editing (if not read-only and upload tab is available)
    if (!readOnly && showExisting && showUploadTab) {
      setActiveTab(0);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setFormData({
      documentName: '',
      targetType: urlType==='assessor' ? 'do' : 'lab',
      description: '',
      uploadedFile: null
    });
    setEditingDocument(null);
    setErrors({});
  };

  // Delete document
  const handleDelete = async (document) => {
    try {
      const response = await additionalDocumentService.deleteDocument(document.id);
      
      if (response.success) {
        setSuccessMessage('Document deleted successfully!');
        await loadExistingDocuments();
        
        if (onDocumentDeleted) {
          onDocumentDeleted(document);
        }
      } else {
        setErrorMessage(response.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setErrorMessage('Error deleting document: ' + error.message);
    } finally {
      setDeleteDialog({ open: false, document: null });
    }
  };

  // View document
  const handleViewDocument = async (document) => {
    if (document.fileKey || document.fileName) {
      const success = await s3Utils.handleFileViewWithS3(
        document.fileKey || document.fileName,
        {
          showError: (msg) => setErrorMessage(msg),
          showLoading: () => setLoading(true),
          hideLoading: () => setLoading(false)
        }
      );
      
      if (!success) {
        setErrorMessage('Failed to open document');
      }
    }
  };

  // Get target type display name
  const getTargetTypeDisplay = (type) => {
    switch (type) {
      case 'lab': return 'Laboratory';
      case 'assessor': return 'Assessor';
      case 'do': return 'Nabl';
      default: return type;
    }
  };

  // Get target type color
  const getTargetTypeColor = (type) => {
    switch (type) {
      case 'lab': return 'primary';
      case 'assessor': return 'secondary';
      case 'do': return 'info';
      default: return 'default';
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear edit state when switching tabs
    if (editingDocument) {
      handleCancelEdit();
    }
  };

  // Load documents on component mount
  useEffect(() => {
    loadExistingDocuments();
  }, [finalOrgId, finalAppId, showExisting]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper 
        elevation={8}
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Cpath d="m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box 
              sx={{ 
                background: 'rgba(255,255,255,0.2)', 
                borderRadius: 2, 
                p: 1.5, 
                mr: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DocumentIcon sx={{ fontSize: 36 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                {title}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
          
          {/* Back Button */}
          {showBackButton && onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }
              }}
            >
              Back
            </Button>
          )}
        </Box>
      </Paper>

      {/* Tabs Navigation */}
      {!readOnly && showExisting && showUploadTab && (
        <Paper 
          elevation={2}
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            background: 'linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                py: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.08)'
                },
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '& .MuiBadge-badge': {
                    backgroundColor: 'white',
                    color: '#667eea'
                  }
                }
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {editingDocument ? <EditIcon /> : <AddIcon />}
                  <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
                    {editingDocument ? 'Edit Document' : 'Add Document'}
                  </Typography>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon />
                  <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
                    Existing Documents
                  </Typography>
                  <Badge 
                    badgeContent={existingDocuments.length} 
                    color="primary" 
                    max={999}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#667eea',
                        color: 'white',
                        fontWeight: 600
                      }
                    }}
                  >
                    <Box sx={{ width: 8 }} />
                  </Badge>
                </Box>
              } 
            />
          </Tabs>
        </Paper>
      )}

      {/* Tab Content */}
      {/* Add Document Tab */}
      {!readOnly && showUploadTab && (!showExisting || activeTab === 0) && (
        <Paper 
          elevation={3}
          sx={{ 
            p: 4, 
            mb: 3, 
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(102, 126, 234, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600,
                color: '#2c3e50'
              }}
            >
              <Box 
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 1.5,
                  p: 1,
                  mr: 2,
                  display: 'flex',
                  color: 'white'
                }}
              >
                {editingDocument ? <EditIcon /> : <AddIcon />}
              </Box>
              {editingDocument ? 'Edit Document' : 'Add New Document'}
            </Typography>
            {editingDocument && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelEdit}
                sx={{ borderRadius: 2 }}
              >
                Cancel Edit
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            {/* Document Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Document Name"
                value={formData.documentName}
                onChange={handleInputChange('documentName')}
                error={!!errors.documentName}
                helperText={errors.documentName}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                placeholder="Enter document name"
              />
            </Grid>

            {/* Target Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.targetType}>
                <InputLabel>Target Type *</InputLabel>
                <Select
                  value={formData.targetType}
                  onChange={handleInputChange('targetType')}
                  label="Target Type *"
                  disabled={urlType === 'assessor'}
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(102, 126, 234, 0.2)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(102, 126, 234, 0.4)'
                    }
                  }}
                >
                  <MenuItem value="lab">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LabIcon sx={{ color: '#2196f3' }} />
                      <Typography>Laboratory</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="assessor">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AssessorIcon sx={{ color: '#9c27b0' }} />
                      <Typography>Assessor</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="do">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <DoIcon sx={{ color: '#ff9800' }} />
                      <Typography>Nabl</Typography>
                    </Box>
                  </MenuItem>
                </Select>
                {errors.targetType && (
                  <FormHelperText>{errors.targetType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                placeholder="Enter additional description or notes about this document"
              />
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <FileUploadComponent
                fieldName="additionalDocument"
                label="Upload Document"
                orgId={finalOrgId}
                section="additional-documents"
                required={!editingDocument}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                maxSize={10}
                onChange={handleFileUpload}
                initialFile={formData.uploadedFile}
                localDeleteOnly={true}
              />
              {errors.file && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {errors.file}
                </Typography>
              )}
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                    px: 5,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #ccc 0%, #999 100%)'
                    }
                  }}
                >
                  {saving ? 'Saving...' : (editingDocument ? 'Update Document' : 'Save Document')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Existing Documents Tab */}
      {showExisting && (readOnly || !showUploadTab || activeTab === 1) && (
        <Paper 
          elevation={3}
          sx={{ 
            p: 4, 
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(102, 126, 234, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 600,
                color: '#2c3e50'
              }}
            >
              <Box 
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 1.5,
                  p: 1,
                  mr: 2,
                  display: 'flex',
                  color: 'white'
                }}
              >
                <DocumentIcon />
              </Box>
              Saved Documents 
              <Chip 
                label={existingDocuments.length}
                size="small"
                sx={{ 
                  ml: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadExistingDocuments}
              disabled={loadingDocuments}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Box>

          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : existingDocuments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DocumentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No documents found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {readOnly ? 'No additional documents have been uploaded' : 'Start by adding your first document above'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '& .MuiTableCell-head': {
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DocumentIcon fontSize="small" />
                        Document Name
                      </Box>
                    </TableCell>
                    <TableCell>Target Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingDocuments.map((document, index) => (
                    <TableRow 
                      key={document.id} 
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#fafbfc' : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.08)',
                          transform: 'scale(1.01)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FileIcon sx={{ color: '#667eea', fontSize: 18 }} />
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                            {document.documentName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTargetTypeDisplay(document.targetType)}
                          color={getTargetTypeColor(document.targetType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {document.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {document.uploadDate ? new Date(document.uploadDate).toLocaleDateString() : 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="textSecondary">
                          {document.uploadedBy || 'Unknown'}
                        </Typography>
                        </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Document" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(document)}
                              sx={{ 
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                color: '#2196f3',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#2196f3',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {(urlType === "do" || urlType === "assessor") && localStorage.getItem('orgId') === document.uploadedById && (
                            <>
                              <Tooltip title="Edit Document" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(document)}
                                  sx={{ 
                                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                    color: '#ff9800',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: '#ff9800',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Delete Document" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDialog({ open: true, document })}
                                  sx={{ 
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                    color: '#f44336',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: '#f44336',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, document: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the document "{deleteDialog.document?.documentName}"?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, document: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteDialog.document)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white' }} />
        </Box>
      )}
    </Box>
  );
};

export default AdditionalDocument;